import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  useOpenerState,
  useStartOpener,
  useStopOpener,
  useClearOpenerPanicLockout,
  useBootOpenerOrchestrator,
  useShutdownOpenerOrchestrator,
} from '../../api/useEntry';
import { useDomainPresence } from 'src/Common/hooks/useDomainPresence';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';
import { VenueMaintenanceControl } from '../shared/VenueMaintenanceControl';
import { deriveOpenerPower, openerPowerTooltip } from './openerPower';
import { HEADER_CONTROL_BTN_SX } from '../shared/headerButtonSx';
import {
  JobProgressStepper,
  JobResultContent,
  buildJobResultSnack,
  JobResultSnack
} from 'src/Common/components/JobProgressStepper/JobProgressStepper';
import { jobSnackAutoHideMs } from 'src/Common/components/JobProgressStepper/jobSnackAutoHide';

/**
 * The opener's power control: ONE state pill and ONE button.
 *
 * It used to be four buttons and two chips reporting three different pods, and
 * the operator's actual question — "can I start turning groups on?" — was
 * answered by none of them. `deriveOpenerPower` collapses the pods into that
 * one fact; the escape hatches (manual boot / shutdown of the control plane)
 * move to the overflow menu, where they belong: they exist for the day the
 * state read itself is frozen, not for daily use.
 */
export const StopStartOpenerButtons = () => {
  // The full query result, not just `data`: `dataUpdatedAt`/`isError` are what
  // tell a live orchestrator apart from a drained one when ROOT cannot be read.
  // `data` alone cannot — `keepPreviousData` holds the last snapshot forever.
  const openerStateQuery = useOpenerState();
  const state = openerStateQuery.data;
  const stopMutation = useStopOpener();
  const startMutation = useStartOpener();
  const clearLockoutMutation = useClearOpenerPanicLockout();
  const bootOrchestratorMutation = useBootOpenerOrchestrator();
  const shutdownOrchestratorMutation = useShutdownOpenerOrchestrator();
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);
  const [confirmShutdownOpen, setConfirmShutdownOpen] = useState(false);
  // Confirmed like Stop and Shutdown. It is the one control that RE-ENABLES
  // trading, and the ordinary post-stop sequence (`stopping` → `locked`) leaves
  // it under a cursor that was on "Apagar opener" a moment earlier — the single
  // primary button means the two now share a pixel.
  const [confirmClearLockoutOpen, setConfirmClearLockoutOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const { currentJob } = useUpdates();
  const isMyJob = Boolean(
    currentJob &&
      currentJob.subsystem === 'opener' &&
      (currentJob.status === 'running' ||
        (currentJob.completedAt && Date.now() - currentJob.completedAt < 5000))
  );

  const [showJobResult, setShowJobResult] = useState(false);
  const prevJobRunning = useRef(false);
  useEffect(() => {
    if (isMyJob) {
      prevJobRunning.current = true;
      setShowJobResult(true);
    } else if (prevJobRunning.current && !isMyJob) {
      prevJobRunning.current = false;
      const timer = setTimeout(() => setShowJobResult(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isMyJob]);

  const showProgress = (isMyJob || showJobResult) && currentJob;

  const stopRunning =
    isMyJob && currentJob?.type === 'stop' && currentJob?.status === 'running';
  const startRunning =
    isMyJob && currentJob?.type === 'start' && currentJob?.status === 'running';

  const startJobRunning = Boolean(startRunning || startMutation.isLoading);
  const stopJobRunning = Boolean(stopRunning || stopMutation.isLoading);

  // Read from `/api/system/all/state`, served by ROOT: `useOpenerState` is
  // served by the opener-orchestrator itself, so it goes stale exactly when
  // the pod is down and can never report that.
  //
  // The fast poll is armed ONLY during a transition, and that restraint is not
  // politeness: `BootDomainGate` is mounted on this same page and debounces a
  // `nodeReady: false` by COUNTING distinct reads of this very query (2 reads
  // AND 10s, sized against the 30s default). Polling at 5s unconditionally
  // would cut the window that protects the operator's unsaved rule table from
  // a 30-60s rollout dip down to ~10s. During a start/stop the operator asked
  // for the teardown, so the faster reaction is the wanted one — and the
  // steady state stays on the shared 30s cadence, with the mutations
  // invalidating this key on success so the pill never waits a full cycle to
  // reflect what the operator just did.
  const { get: getDomainPresence, firstAttemptSettled } = useDomainPresence(
    startJobRunning || stopJobRunning ? { refetchIntervalMs: 5_000 } : undefined
  );
  const openerPresence = getDomainPresence('opener');

  const lockout = state?.panicLockout;

  const power = deriveOpenerPower({
    // Nothing is known until the presence query SETTLES once — every field
    // reads `null` before that, indistinguishable from "the backend could not
    // read it", and claiming a state there painted `OPENER ?` with an enabled
    // Start over a healthy opener on every page mount. A failed attempt counts
    // as settled: this query is served by ROOT, a different pod from the one
    // these buttons act on, so its outage must not hold the header inert.
    firstReadDone: firstAttemptSettled,
    booted: openerPresence.booted,
    // Consulted only when root cannot be read. The last attempt on the
    // orchestrator's OWN state query succeeded ⇒ the pod is answering, and the
    // stop route shares its axios instance, so Apagar is reachable.
    controlPlaneAnswering:
      openerStateQuery.dataUpdatedAt > 0 && !openerStateQuery.isError,
    nodeReady: openerPresence.nodeReady,
    nodeServing: openerPresence.nodeServing,
    groupEnginesRunning: state?.groupEnginesRunning,
    // Passed RAW: the "a drained pod's lockout snapshot is stale" rule lives in
    // `deriveOpenerPower`'s precedence, so there is exactly one place it can be
    // got wrong.
    lockoutArmed: Boolean(lockout?.armed),
    lockoutExpiresLabel: lockout?.expiresAt
      ? new Date(lockout.expiresAt).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      : undefined,
    startJobRunning,
    stopJobRunning
  });

  const busy = power.busy || clearLockoutMutation.isLoading;

  const primaryIcon = {
    start: <PlayArrowIcon />,
    stop: <PowerSettingsNewIcon />,
    'clear-lockout': <LockOpenIcon />,
    none: <PowerSettingsNewIcon />
  }[power.action];

  const primaryColor = {
    start: 'success',
    stop: 'warning',
    'clear-lockout': 'error',
    none: 'inherit'
  }[power.action] as 'success' | 'warning' | 'error' | 'inherit';

  const runPrimary = () => {
    if (power.action === 'start') startMutation.mutate();
    else if (power.action === 'stop') setConfirmStopOpen(true);
    else if (power.action === 'clear-lockout') setConfirmClearLockoutOpen(true);
  };

  const [resultSnack, setResultSnack] = useState<JobResultSnack | null>(null);

  // Watch for job completion AFTER it was mine. The popover already shows
  // step-by-step progress; this is the "did it actually finish" toast that
  // survives even if the popover auto-dismisses or the user has navigated.
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (
      currentJob &&
      currentJob.subsystem === 'opener' &&
      prev === 'running' &&
      (currentJob.status === 'completed' || currentJob.status === 'failed')
    ) {
      setResultSnack(buildJobResultSnack('Opener', currentJob));
    }
    prevStatusRef.current = currentJob?.status;
  }, [currentJob]);

  const closeMenu = () => setMenuAnchor(null);

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <VenueMaintenanceControl />
          <Tooltip
            title={
              openerPowerTooltip(power.phase) +
              (power.phase === 'locked' && lockout?.expiresAt
                ? ` Expira ${new Date(lockout.expiresAt).toLocaleString()}.`
                : '') +
              (power.phase === 'off' && state?.lastStopAt
                ? ` Último stop ${new Date(state.lastStopAt).toLocaleTimeString()}.`
                : '')
            }
            placement="top"
          >
            <Chip
              size="small"
              label={power.label}
              color={power.color}
              variant={power.phase === 'off' ? 'outlined' : 'filled'}
              icon={
                power.busy ? (
                  <CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />
                ) : undefined
              }
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
          <Tooltip title={openerPowerTooltip(power.phase)} placement="top">
            <span>
              <Button
              variant="outlined"
              color={primaryColor}
              size="small"
              startIcon={
                busy ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  primaryIcon
                )
              }
              onClick={runPrimary}
              disabled={busy || power.action === 'none'}
              sx={HEADER_CONTROL_BTN_SX}
            >
              {power.actionLabel}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Controles avanzados del control plane" placement="top">
            <IconButton
              size="small"
              aria-label="Controles avanzados del opener"
              onClick={event => setMenuAnchor(event.currentTarget)}
              sx={{ p: 0.25 }}
            >
              {bootOrchestratorMutation.isLoading ||
              shutdownOrchestratorMutation.isLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <MoreVertIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
          {/*
            Escape hatches, deliberately one click away. `useOpenerState` is
            served BY the control pod, so once it is down the header's own
            state freezes — Prender does boot it, but if even that read is
            unreliable these two are the way back. They must therefore stay
            reachable in EVERY phase, including a stale locked one.
          */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                bootOrchestratorMutation.mutate();
              }}
              disabled={bootOrchestratorMutation.isLoading}
            >
              <ListItemIcon>
                <CloudSyncIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Boot control plane"
                secondary="Levanta el opener-orchestrator sin arrancar el trading"
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.65rem' }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                setConfirmShutdownOpen(true);
              }}
              disabled={shutdownOrchestratorMutation.isLoading}
            >
              <ListItemIcon>
                <CloudOffIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Shutdown control plane"
                secondary="Baja el orchestrator ya, sin tocar el trading"
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.65rem' }}
              />
            </MenuItem>
          </Menu>
        </Stack>

        {showProgress && currentJob && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              mt: 1,
              zIndex: 1400
            }}
          >
            <Paper elevation={6} sx={{ p: 1.5, minWidth: 280, maxWidth: 340, bgcolor: 'background.paper' }}>
              <JobProgressStepper job={currentJob} label="Opener" />
            </Paper>
          </Box>
        )}
      </Box>

      <Snackbar
        open={Boolean(resultSnack)}
        autoHideDuration={jobSnackAutoHideMs(resultSnack)}
        onClose={() => setResultSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {resultSnack ? (
          <Alert
            severity={resultSnack.kind}
            onClose={() => setResultSnack(null)}
            sx={{ fontSize: '0.8rem', maxWidth: 360 }}
          >
            <JobResultContent snack={resultSnack} />
          </Alert>
        ) : undefined}
      </Snackbar>

      {/*
        Boot/shutdown failures are NOT surfaced here: the app-wide
        `MutationCache.onError` already toasts them with copy keyed on
        `opener:boot-orchestrator` / `opener:shutdown-orchestrator` (plus a
        Sentry capture). A second Snackbar would both duplicate that message
        and collide with the job-result one below — same anchorOrigin, and MUI
        does not stack independent Snackbars.
      */}
      <Dialog
        open={confirmStopOpen}
        onClose={() => setConfirmStopOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Apagar opener</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Esto va a:
            <ul style={{ margin: '4px 0', paddingLeft: '1.2em' }}>
              <li>Cancelar todas las órdenes abiertas en Kalshi y Polymarket.</li>
              <li>Pausar todas las reglas del opener.</li>
              <li>Armar un lockout de 24h (bloquea prender hasta limpiarlo a mano).</li>
              <li>
                Liberar el control plane: root baja el opener-orchestrator y el
                CRUD de reglas solo (~30s) salvo que algo más los esté
                sosteniendo — predictions prendido, un grupo todavía arriba, o
                un boot manual sin cerrar. Mientras sigan arriba, el estado va a
                decir BLOQUEADO en vez de OFF.
              </li>
            </ul>
            OJO: las órdenes de la UI / proxy-wallet de Polymarket NO se cancelan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStopOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmStopOpen(false);
              stopMutation.mutate();
            }}
            color="warning"
            variant="contained"
            startIcon={<PowerSettingsNewIcon />}
          >
            Apagar opener
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmClearLockoutOpen}
        onClose={() => setConfirmClearLockoutOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Limpiar lockout de 24h</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            El lockout es lo que impide arrancar grupos y activar reglas después
            de un stop o un panic. Limpiarlo NO prende nada por sí solo, pero
            deja el opener listo para volver a operar.
            <ul style={{ margin: '4px 0', paddingLeft: '1.2em' }}>
              <li>Revisá antes por qué se armó.</li>
              <li>Las reglas siguen pausadas: hay que prenderlas a mano.</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmClearLockoutOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmClearLockoutOpen(false);
              clearLockoutMutation.mutate();
            }}
            color="error"
            variant="contained"
            startIcon={<LockOpenIcon />}
          >
            Limpiar lockout
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Confirmed for the same reason Apagar opener is: this drains the pod that
        serves the page you are on, and the backend releases EVERY claim on it
        — including the predictions-start one, which belongs to a lifecycle
        this screen knows nothing about.
      */}
      <Dialog
        open={confirmShutdownOpen}
        onClose={() => setConfirmShutdownOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Shutdown control plane</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Apaga el opener-orchestrator. No toca el trading — para eso está
            Apagar opener.
            <ul style={{ margin: '4px 0', paddingLeft: '1.2em' }}>
              <li>El CRUD de reglas deja de responder hasta bootearlo.</li>
              <li>
                Libera TODOS los claims sobre el pod, incluido el de un
                predictions-start en curso.
              </li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmShutdownOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmShutdownOpen(false);
              shutdownOrchestratorMutation.mutate();
            }}
            color="warning"
            variant="contained"
            startIcon={<CloudOffIcon />}
          >
            Shutdown
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
